import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ChevronRight, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ICON = {
  car_booked: "🚗", car_completed: "🎉", car_promo: "🏷️",
  transfer_upsell: "🌴", car_review: "🚙", car_provider_review: "🔑",
  refund: "💸", refund_claim: "🧾", wallet: "⭐", trip_chat: "💬", broadcast: "📣",
  host_signup: "🧑‍✈️", tour_added: "🗺️", payout_request: "💰",
  host_status: "✅", email_verified: "✅", password_reset: "🔑",
};

// Admin task notifications that are considered "done" simply by reviewing them.
const COMPLETE_ON_CLICK = new Set(["tour_added", "payout_request"]);

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function notifRoute(n, user) {
  const d = n.data || {};
  if (d.link) return d.link;
  const dash = user?.role === "provider" ? "/provider" : "/dashboard";
  switch (n.type) {
    case "car_booked": return d.booking_id ? `/car-booking/${d.booking_id}/confirmation` : dash;
    case "car_completed": return "/dashboard";
    case "car_promo": return "/cars";
    case "car_review":
    case "car_provider_review":
    case "refund_claim": return "/provider";
    case "transfer_upsell": return "/browse";
    case "refund":
    case "wallet": return "/dashboard";
    case "trip_chat": return d.booking_id ? `${dash}?chat=${d.booking_id}` : dash;
    case "broadcast": return "/browse";
    default: return dash;
  }
}

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = () => {
    api.get("/notifications")
      .then((r) => { setItems(r.data.notifications); setUnread(r.data.unread); })
      .catch(() => {});
  };

  useEffect(() => {
    if (!user) return;
    load();
    // Near real-time: admins poll faster to surface approvals/payouts quickly.
    const interval = user.role === "admin" ? 15000 : 45000;
    const t = setInterval(load, interval);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      load();
      if (unread > 0) api.post("/notifications/read-all").then(() => setUnread(0)).catch(() => {});
    }
  };

  const openNotification = (n) => {
    setOpen(false);
    if (!n.read) {
      api.post(`/notifications/${n.id}/read`).catch(() => {});
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    if (n.task && n.status === "pending" && COMPLETE_ON_CLICK.has(n.type)) {
      api.post(`/notifications/${n.id}/complete`).catch(() => {});
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, status: "completed" } : x)));
    }
    navigate(notifRoute(n, user));
  };

  if (!user) return null;

  const active = items.filter((n) => n.status !== "completed");
  const completed = items.filter((n) => n.status === "completed");

  const renderRow = (n, done) => (
    <button
      key={n.id}
      type="button"
      data-testid={`notification-${n.type}`}
      onClick={() => openNotification(n)}
      className={`group w-full text-left rounded-xl px-3 py-2.5 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:bg-cyan-400/10 hover:shadow-[0_0_18px_-4px_rgba(34,211,238,0.55)] ${done ? "opacity-60" : n.read ? "" : "bg-cyan-400/5"}`}
    >
      <div className="flex gap-2.5 items-start">
        <span className="text-lg leading-none mt-0.5">{ICON[n.type] || "🔔"}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white leading-snug flex items-center gap-1.5">
            {done && <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" data-testid="notif-completed-check" />}
            {n.title}
          </p>
          <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{n.body}</p>
          {n.data?.promo_code && (
            <span className="inline-block mt-1.5 text-[11px] font-mono-caption bg-fuchsia-500/15 text-fuchsia-300 rounded px-2 py-0.5">{n.data.promo_code}</span>
          )}
          <p className="text-[10px] text-slate-600 mt-1">{timeAgo(n.created_at)}</p>
        </div>
        {!done && <ChevronRight className="w-4 h-4 text-slate-600 mt-0.5 shrink-0 transition-all group-hover:text-cyan-400 group-hover:translate-x-0.5" />}
      </div>
    </button>
  );

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notification-bell"
        className="relative w-9 h-9 rounded-full glass flex items-center justify-center text-slate-300 hover:text-cyan-400 transition-colors">
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <>
            <span className="absolute -top-1 -right-1 w-[18px] h-[18px] rounded-full bg-[#FF2D78] animate-ping opacity-75" />
            <span data-testid="notification-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-gradient-to-br from-[#FF2D78] to-[#FF8A1E] text-white text-[10px] font-bold flex items-center justify-center shadow-[0_0_10px_rgba(255,45,120,0.8)]">
              {unread > 9 ? "9+" : unread}
            </span>
          </>
        )}
      </button>

      {open && (
        <div data-testid="notification-dropdown" className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto glass neon-border rounded-2xl p-2 z-[70] shadow-2xl">
          <div className="px-3 py-2 flex items-center justify-between">
            <p className="font-heading font-bold text-white text-sm">Notifications</p>
            {unread > 0 && <span className="text-[10px] font-bold text-[#FF5E7E]">{unread} new</span>}
          </div>
          {items.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-8">No notifications yet.</p>
          ) : (
            <div className="space-y-1">
              {active.map((n) => renderRow(n, false))}
              {completed.length > 0 && (
                <>
                  <div className="flex items-center gap-2 px-3 pt-3 pb-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                    <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold" data-testid="notif-completed-header">Completed</p>
                  </div>
                  {completed.map((n) => renderRow(n, true))}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
